import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Card, CardActionArea, Chip, Stack, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useRef,
} from 'react';
import type { ChartId } from 'charts/catalog/chartCatalog';
import { ChartExportMenu } from 'charts/export/ChartExportMenu';
import { CHART_EXPORT_IGNORE_ATTRIBUTE } from 'charts/export/exporters';
import { useNavigate } from 'react-router-dom';
import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import { ChartCardHeading } from './ChartCardHeading';
import { pillChipSx } from './pillChipSx';

const cardSx = {
  height: '100%',
  minHeight: { xs: 'unset', sm: '420px' },
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '4px',
  border: `1px solid ${Color.solidGray}`,
  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.08)',
  bgcolor: Color.white,
  overflow: 'visible',
} satisfies SxProps<Theme>;

const cardInnerSx = {
  p: '20px',
  flex: 1,
  minHeight: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
} satisfies SxProps<Theme>;

/** The chart opens its details; the heading holds the tile's own buttons, so it stays outside. */
const chartActionAreaSx = {
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  mt: 1.5,
  borderRadius: '4px',
} satisfies SxProps<Theme>;

type SmallChartCardProps = PropsWithChildren<{
  chartId: ChartId;
  title: string;
  subtitle?: string;
  loading: boolean;
  /** The chart has its own filters, set in its details: they override the section filters. */
  hasCustomFilters?: boolean;
  /** Gives the chart the section filters back, with the × of its filters chip. */
  onRestoreSectionFilters?: () => void;
  /** The tile exports its chart; off for a tile showing only part of it (the inspection summary). */
  exportable?: boolean;
}>;

function CustomFiltersChip({ onRestoreSectionFilters }: Readonly<{ onRestoreSectionFilters?: () => void }>): ReactElement {
  const intl = useIntl();
  return (
    <Tooltip title={formatTranslation(intl, AnalyticsTranslationKeys.customFiltersTooltip)}>
      <Chip
        icon={<FilterListIcon />}
        variant="outlined"
        sx={pillChipSx}
        label={formatTranslation(intl, AnalyticsTranslationKeys.customFilters)}
        onDelete={onRestoreSectionFilters}
        deleteIcon={
          <CloseIcon aria-label={formatTranslation(intl, AnalyticsTranslationKeys.customFiltersRestore)} />
        }
      />
    </Tooltip>
  );
}

/** A chart on the dashboard: opens its details; shows when it has its own filters and exports it. */
export function SmallChartCard({
  children,
  chartId,
  title,
  subtitle,
  loading,
  hasCustomFilters = false,
  onRestoreSectionFilters,
  exportable = true,
}: SmallChartCardProps): ReactElement {
  const navigate = useNavigate();
  const snapshotRef = useRef<HTMLDivElement>(null);

  const redirectTo = useCallback(() => {
    if (!loading) {
      navigate(`/analytics/${chartId}`);
    }
  }, [chartId, loading, navigate]);

  return (
    <Card sx={cardSx} elevation={0}>
      <Box ref={snapshotRef} sx={cardInnerSx}>
        <ChartCardHeading
          chartId={chartId}
          title={title}
          subtitle={subtitle}
          actions={
            <Stack direction='row' alignItems='center' gap={1} {...{ [CHART_EXPORT_IGNORE_ATTRIBUTE]: true }}>
              {hasCustomFilters ? <CustomFiltersChip onRestoreSectionFilters={onRestoreSectionFilters} /> : null}
              {exportable ? (
                <ChartExportMenu chartId={chartId} title={title} snapshotRef={snapshotRef} variant='icon' />
              ) : null}
            </Stack>
          }
        />
        <CardActionArea
          onClick={redirectTo}
          disabled={loading}
          sx={chartActionAreaSx}
          aria-label={title}
        >
          {children}
        </CardActionArea>
      </Box>
    </Card>
  );
}
